import { useCallback, useState } from 'react';
import { useMutation, gql } from '@apollo/client';
import { UserPreferencesModel } from '@utmost.co/enterprise-models';
import { UserPreferencesModelInput } from '@utmost.co/enterprise-models';
import { useLoggedInUser } from 'src/utils/hooks/useLoggedInUser';
import { TutorialsContextProps, TutorialType } from './TutorialsContext';

export const TUTORIAL_KEY = 'tutorials_disabled';

export const UPDATE_TUTORIALS = gql`
  mutation UPDATE_TUTORIALS($userPreferences: UserPreferencesModelInput) {
    patchUserPreferences(userPreferences: $userPreferences) {
      finishedTutorials
    }
  }
`;

export function useTutorials(): TutorialsContextProps {
  const user = useLoggedInUser();
  const initialFinishedTuts = user?.preferences?.finishedTutorials as TutorialType[];

  const [closedTutorials, setClosedTutorials] = useState<TutorialType[]>(initialFinishedTuts || []);
  const [activeTutorial, setActiveTutorial] = useState<TutorialType | null>(null);
  const [updateTutorials] = useMutation<
    { patchUserPreferences: UserPreferencesModel },
    { userPreferences: DeepPartial<UserPreferencesModelInput> }
  >(UPDATE_TUTORIALS);

  const onOpenTutorial = useCallback(
    (tutorial: TutorialType) => {
      if (!closedTutorials.includes(tutorial)) {
        setActiveTutorial(tutorial);
      }
    },
    [closedTutorials]
  );

  const closeTutorial = useCallback(
    async (tutorial: TutorialType, dontShowAgain: boolean) => {
      if (dontShowAgain) {
        const allClosedTutorials = [...closedTutorials, tutorial];
        await updateTutorials({ variables: { userPreferences: { finishedTutorials: allClosedTutorials } } });
        setClosedTutorials(allClosedTutorials);
      }

      setActiveTutorial(null);
    },
    [updateTutorials, closedTutorials]
  );

  return {
    activeTutorial,
    openTutorial: onOpenTutorial,
    closeTutorial
  };
}
